/**
 * Common Receipt Header Generator
 * Used across all receipt types for consistent branding
 */

// Get college info from localStorage or API
export const getCollegeInfo = () => {
  try {
    const storedInfo = localStorage.getItem('collegeInfo');
    if (storedInfo) {
      return JSON.parse(storedInfo);
    }
  } catch (e) {
    console.error('Error parsing college info:', e);
  }
  return null;
};

// Store college info in localStorage — strip heavy fields (logo_base64, etc.)
// to avoid QuotaExceededError when the logo is embedded as data URI.
export const setCollegeInfo = (info) => {
  try {
    if (!info) return;
    // Drop anything that looks like a heavy base64 payload or a large blob.
    const { logo_base64, logoBase64, banner_base64, ...safe } = info || {};
    // Also cap any stringified blob that somehow slipped in under a different key.
    Object.keys(safe).forEach((k) => {
      const v = safe[k];
      if (typeof v === 'string' && v.length > 100000) {
        delete safe[k];
      }
    });
    localStorage.setItem('collegeInfo', JSON.stringify(safe));
  } catch (e) {
    // Quota or serialization error — gracefully degrade by clearing the key
    try { localStorage.removeItem('collegeInfo'); } catch {}
    console.warn('collegeInfo not cached (quota or serialization issue):', e?.message || e);
  }
};

// Fetch college info from API
export const fetchCollegeInfo = async (apiBaseUrl, token) => {
  try {
    const response = await fetch(`${apiBaseUrl}/api/tenants/my-college/settings`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    if (response.ok) {
      const data = await response.json();
      // Map fields for consistent naming
      const collegeInfo = {
        ...data,
        college_name: data.name || data.college_name,
        logo_url: data.logo || data.logo_url
      };
      setCollegeInfo(collegeInfo);
      return collegeInfo;
    }
  } catch (e) {
    console.error('Error fetching college info:', e);
  }
  return null;
};

/**
 * Generate receipt header with college logo, name, and address
 * @param {jsPDF} doc - jsPDF document instance
 * @param {Object} collegeInfo - College information object
 * @param {string} receiptTitle - Title of the receipt (e.g., "FEE RECEIPT")
 * @param {string} logoBase64 - Base64 encoded logo image (optional)
 * @returns {number} - Y position after header
 */
export const generateReceiptHeader = async (doc, collegeInfo, receiptTitle = 'RECEIPT', logoBase64 = null) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 20;
  let yPos = 15;

  // Get college info if not provided
  if (!collegeInfo) {
    collegeInfo = getCollegeInfo() || {};
  }

  const collegeName = collegeInfo.college_name || collegeInfo.name || 'Institution';
  const collegeAddress = collegeInfo.address || '';
  const collegeCity = collegeInfo.city || '';
  const collegeState = collegeInfo.state || '';
  const collegePincode = collegeInfo.pincode || collegeInfo.pin_code || '';
  const collegePhone = collegeInfo.phone || collegeInfo.mobile || '';
  const collegeEmail = collegeInfo.email || '';
  const collegeWebsite = collegeInfo.website || '';
  const collegeLogo = logoBase64 || collegeInfo.logo_base64 || collegeInfo.logo_url || collegeInfo.logo || '';

  // Add logo if available (base64 or URL converted to base64)
  let logoAdded = false;
  if (collegeLogo) {
    try {
      const logoWidth = 20;
      const logoHeight = 20;
      const logoX = margin;
      
      if (collegeLogo.startsWith('data:image')) {
        // Base64 image
        doc.addImage(collegeLogo, 'PNG', logoX, yPos - 5, logoWidth, logoHeight);
        logoAdded = true;
      } else if (collegeLogo.startsWith('http')) {
        // Store the logo URL for reference - actual image should be pre-converted to base64
        // Logo will be fetched and cached by the calling component
      }
    } catch (e) {
      console.log('Could not add logo:', e);
    }
  }

  // Adjust text position if logo was added
  const textStartX = logoAdded ? margin + 25 : pageWidth / 2;
  const textAlign = logoAdded ? 'left' : 'center';

  // College Name - Bold and prominent
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 51, 102); // Dark blue
  
  if (logoAdded) {
    doc.text(collegeName.toUpperCase(), textStartX, yPos + 2);
  } else {
    doc.text(collegeName.toUpperCase(), pageWidth / 2, yPos, { align: 'center' });
  }
  yPos += 7;

  // Full Address
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(60, 60, 60);

  // Build full address string
  const addressParts = [];
  if (collegeAddress) addressParts.push(collegeAddress);
  
  const cityStatePincode = [collegeCity, collegeState, collegePincode].filter(Boolean).join(', ');
  if (cityStatePincode) addressParts.push(cityStatePincode);

  // Calculate max width for text (considering logo space)
  const maxTextWidth = logoAdded ? (pageWidth - margin - textStartX - 5) : (pageWidth - 2 * margin);

  // Print address lines with text wrapping for long addresses
  addressParts.forEach((line) => {
    if (line) {
      // Split long text into multiple lines
      const wrappedLines = doc.splitTextToSize(line, maxTextWidth);
      wrappedLines.forEach((wrappedLine) => {
        if (logoAdded) {
          doc.text(wrappedLine, textStartX, yPos);
        } else {
          doc.text(wrappedLine, pageWidth / 2, yPos, { align: 'center' });
        }
        yPos += 4;
      });
    }
  });

  // Phone and Email on same line
  const contactInfo = [];
  if (collegePhone) contactInfo.push(`Phone: ${collegePhone}`);
  if (collegeEmail) contactInfo.push(`Email: ${collegeEmail}`);
  
  if (contactInfo.length > 0) {
    doc.setFontSize(8);
    const contactText = contactInfo.join('  |  ');
    // Wrap contact info if too long
    const wrappedContact = doc.splitTextToSize(contactText, maxTextWidth);
    wrappedContact.forEach((line) => {
      if (logoAdded) {
        doc.text(line, textStartX, yPos);
      } else {
        doc.text(line, pageWidth / 2, yPos, { align: 'center' });
      }
      yPos += 4;
    });
  }

  // Ensure yPos is below logo if logo was added
  if (logoAdded) {
    yPos = Math.max(yPos, 35);
  }

  // Website on separate line with wrapping
  if (collegeWebsite) {
    doc.setFontSize(8);
    doc.setTextColor(0, 102, 204); // Blue color for website
    const websiteText = collegeWebsite.startsWith('http') ? collegeWebsite : `www.${collegeWebsite}`;
    const wrappedWebsite = doc.splitTextToSize(websiteText, pageWidth - 2 * margin);
    wrappedWebsite.forEach((line) => {
      doc.text(line, pageWidth / 2, yPos, { align: 'center' });
      yPos += 4;
    });
    doc.setTextColor(60, 60, 60);
    yPos += 2;
  } else {
    yPos += 2;
  }

  // Decorative line
  doc.setDrawColor(0, 51, 102);
  doc.setLineWidth(1);
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 2;
  doc.setLineWidth(0.3);
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 8;

  // Receipt Title
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text(receiptTitle, pageWidth / 2, yPos, { align: 'center' });
  yPos += 3;

  // Underline for title
  const titleWidth = doc.getTextWidth(receiptTitle);
  doc.setLineWidth(0.5);
  doc.line((pageWidth - titleWidth) / 2 - 5, yPos, (pageWidth + titleWidth) / 2 + 5, yPos);
  yPos += 10;

  // Reset text color
  doc.setTextColor(0, 0, 0);

  return yPos;
};

/**
 * Fetch and convert logo URL to base64
 * @param {string} logoUrl - URL of the logo image
 * @returns {Promise<string|null>} - Base64 encoded image or null
 */
export const fetchLogoAsBase64 = async (logoUrl) => {
  if (!logoUrl || !logoUrl.startsWith('http')) return null;
  
  try {
    const response = await fetch(logoUrl);
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    console.error('Error fetching logo:', e);
    return null;
  }
};

/**
 * Generate receipt footer with signature area and disclaimer
 * @param {jsPDF} doc - jsPDF document instance
 * @param {Object} collegeInfo - College information object
 */
export const generateReceiptFooter = (doc, collegeInfo, termsText) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;

  const collegeName = collegeInfo?.college_name || collegeInfo?.name || 'Institution';

  // Optional: Render tenant-specific Terms & Conditions block above the signature row
  if (termsText && typeof termsText === 'string' && termsText.trim()) {
    // Strip HTML tags to keep PDF simple (tiptap produces <p>, <ul>, <li>, etc.)
    const plain = termsText
      .replace(/<\s*br\s*\/?\s*>/gi, '\n')
      .replace(/<\/(p|li|div|h[1-6])>/gi, '\n')
      .replace(/<li[^>]*>/gi, '• ')
      .replace(/<[^>]+>/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
    if (plain) {
      const termsStartY = pageHeight - 90;
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(60, 60, 60);
      doc.text('Terms & Conditions:', margin, termsStartY);
      doc.setFontSize(7.5);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(80, 80, 80);
      const wrapped = doc.splitTextToSize(plain, pageWidth - 2 * margin);
      const maxLines = 6;
      const linesToShow = wrapped.slice(0, maxLines);
      doc.text(linesToShow, margin, termsStartY + 4);
    }
  }

  // Footer - Authorized Signature
  const footerY = pageHeight - 45;
  
  // Cashier/Accountant signature on left
  doc.setLineWidth(0.3);
  doc.line(margin, footerY, margin + 50, footerY);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text('Cashier/Accountant', margin + 25, footerY + 5, { align: 'center' });

  // Authorized Signature on right
  doc.line(pageWidth - margin - 50, footerY, pageWidth - margin, footerY);
  doc.text('Authorized Signatory', pageWidth - margin - 25, footerY + 5, { align: 'center' });

  // College stamp area in center
  doc.setDrawColor(150, 150, 150);
  doc.setLineDashPattern([2, 2], 0);
  doc.rect(pageWidth / 2 - 20, footerY - 20, 40, 25);
  doc.setLineDashPattern([], 0);
  doc.setFontSize(7);
  doc.setTextColor(150, 150, 150);
  doc.text('College Seal', pageWidth / 2, footerY - 5, { align: 'center' });

  // Disclaimer
  doc.setFontSize(7);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(100, 100, 100);
  doc.text('This is a computer-generated receipt. Subject to realization of cheque/online payment.', pageWidth / 2, pageHeight - 20, { align: 'center' });
  doc.text(`© ${new Date().getFullYear()} ${collegeName}. All rights reserved.`, pageWidth / 2, pageHeight - 15, { align: 'center' });
  doc.text('For any queries, please contact the accounts department.', pageWidth / 2, pageHeight - 10, { align: 'center' });
};

/**
 * Fetch terms_html for a receipt type for current tenant (authenticated).
 * Returns '' on any failure to keep receipt printing resilient.
 * Cached in-memory per page-load.
 */
const _termsCache = {};
export const fetchReceiptTerms = async (receiptType) => {
  if (_termsCache[receiptType] !== undefined) return _termsCache[receiptType];
  try {
    const apiBaseUrl = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem('token');
    const res = await fetch(`${apiBaseUrl}/api/master-settings/receipt-terms`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!res.ok) { _termsCache[receiptType] = ''; return ''; }
    const data = await res.json();
    const val = (data && data[receiptType] && data[receiptType].terms_html) || '';
    _termsCache[receiptType] = val;
    return val;
  } catch (_) {
    _termsCache[receiptType] = '';
    return '';
  }
};

export default {
  generateReceiptHeader,
  generateReceiptFooter,
  getCollegeInfo,
  setCollegeInfo,
  fetchCollegeInfo,
  fetchLogoAsBase64,
  fetchReceiptTerms
};
